import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import styles from './apiClientList.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import Loading from '@/components/Loading/Loading.tsx'
import { ApiClient } from '@/types/ApiClient.ts'
import { GET_API_CLIENTS_LIST } from '@/modules/API/apiClient/getApiClient.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import Mobile from '@/modules/desktopMobile/mobile.tsx'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import { GridColDef } from '@mui/x-data-grid'
import { ArrowBack, Check, Close, RemoveRedEye } from '@mui/icons-material'
import HolidayVillage from '@mui/icons-material/HolidayVillage'
import { Daycare } from '@/types/Daycare.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import moment from 'moment/moment'
import DaycareListDialog from '@/container/group/groupList/Dialog/daycareListDialog.tsx'
import CreateClientApiDialog from '@/container/settings/apiClient/Dialog/createClientApiDialog.tsx'
import ApiClientProfile from '@/container/settings/apiClient/apiClientProfile/apiClientProfile.tsx'

interface ApiClientListArgs {
    backToParameters: () => void
}

const ApiClientList: FunctionComponent<ApiClientListArgs> = ({ backToParameters }: ApiClientListArgs) => {
    const { t } = useTranslation()

    const { showNotificationSuccess } = useNotification()

    const [isAddApiClientDialogOpen, setAddApiClientDialogOpen] = useState<boolean>(false)

    const [isDaycareListDialogOpen, setIsDaycareListDialogOpen] = useState<boolean>(false)
    const [showDaycares, setShowDaycares] = useState<Daycare[]>([])

    const [selectedApiClient, setSelectedApiClient] = useState<ApiClient | null>(null)

    const [apiClientList, setApiClientList] = useState<ApiClient[]>([])
    const { loading: loadingApiClient, data: dataApiClient, refetch } = useQuery(GET_API_CLIENTS_LIST)

    useEffect(() => {
        if (dataApiClient) {
            setApiClientList(dataApiClient.listApiClients)
        }
    }, [dataApiClient])

    const headerClassName: string = styles.HeaderDaycare

    const trusteeColumnsCALDWeb: GridColDef[] = [
        {
            field: 'structureCount',
            headerName: t('structureCount'),
            headerClassName: headerClassName,
            width: 80,
            maxWidth: 250,
            minWidth: 80,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openDaycareList(params.row.daycares)}>
                    {params?.row.daycares ? params.row.daycares.length : 0}
                    <HolidayVillage className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'id',
            headerName: t('adminID'),
            headerClassName: headerClassName,
            width: 30,
            maxWidth: 50,
            minWidth: 30,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'clientId',
            headerName: t('clientId'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 300,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'lastUsed',
            headerName: t('lastUsed'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 500,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.lastUsed ? (
                        <p className={styles.Text}>
                            {moment(params.row.lastUsed, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm')}
                        </p>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'active',
            headerName: t('daycareActive'),
            headerClassName: headerClassName,
            width: 50,
            maxWidth: 150,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.CheckContainer}>
                    {params.row.active ? <Check className={styles.GreenIcon} /> : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <RemoveRedEye className={styles.Icon} onClick={() => handleSelectApiClient(params.row.id)} />
                </div>
            )
        }
    ]

    const trusteeColumnsCALMobile: GridColDef[] = [
        {
            field: 'structureCount',
            headerName: t('structures'),
            headerClassName: headerClassName,
            width: 80,
            maxWidth: 250,
            minWidth: 80,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openDaycareList(params.row.daycares)}>
                    {params?.row.daycares ? params.row.daycares.length : 0}
                    <HolidayVillage className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'id',
            headerName: t('adminID'),
            headerClassName: headerClassName,
            width: 30,
            maxWidth: 50,
            minWidth: 30,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 300,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <RemoveRedEye className={styles.Icon} onClick={() => handleSelectApiClient(params.row.id)} />
                </div>
            )
        }
    ]

    function handleSelectApiClient(apiClientId: number): void {
        setSelectedApiClient(apiClientList.find((client: ApiClient): boolean => client.id === apiClientId) ?? null)
    }

    function openDaycareList(daycares: Daycare[]): void {
        if (daycares && daycares.length > 0) {
            setShowDaycares(daycares)
            setIsDaycareListDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    function backToList(): void {
        refetch()
        setSelectedApiClient(null)
    }

    return (
        <>
            {selectedApiClient ? (
                <ApiClientProfile id={selectedApiClient.id} backToList={backToList} />
            ) : (
                <div className={styles.Container}>
                    <div className={styles.TitleAndButton}>
                        <div className={styles.Title}>
                            <Title text={t('apiClient')} className={styles.Title} />
                        </div>
                        <ButtonTextIcon
                            type={'create'}
                            onClick={() => setAddApiClientDialogOpen(true)}
                            className={styles.Button}
                        />
                        {isAddApiClientDialogOpen && (
                            <CreateClientApiDialog
                                open={isAddApiClientDialogOpen}
                                handleClose={() => setAddApiClientDialogOpen(false)}
                                refetchClientApi={refetch}
                            />
                        )}
                    </div>
                    {loadingApiClient && <Loading overall={false} />}
                    {dataApiClient && (
                        <div className={styles.ApiClientInformations}>
                            {apiClientList.length === 0 ? (
                                <div className={styles.TextContainer}>{t('noApiClient')}</div>
                            ) : (
                                <>
                                    <Desktop>
                                        <StyledDataGrid
                                            autoHeight
                                            columns={trusteeColumnsCALDWeb}
                                            rows={apiClientList}
                                            disableDensitySelector
                                            disableColumnMenu
                                            slots={{ toolbar: QuickSearchToolbar }}
                                            showToolbar
                                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                                            rowHeight={80}
                                            className={styles.DataGrid}
                                        />
                                    </Desktop>
                                    <Mobile>
                                        <StyledDataGrid
                                            autoHeight
                                            columns={trusteeColumnsCALMobile}
                                            rows={apiClientList}
                                            disableDensitySelector
                                            disableColumnMenu
                                            slots={{ toolbar: QuickSearchToolbar }}
                                            showToolbar
                                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                                            rowHeight={80}
                                            className={styles.DataGrid}
                                        />
                                    </Mobile>
                                </>
                            )}
                        </div>
                    )}
                    {isDaycareListDialogOpen && (
                        <DaycareListDialog
                            isDialogOpen={isDaycareListDialogOpen}
                            handleCloseDialog={() => setIsDaycareListDialogOpen(false)}
                            daycares={showDaycares}
                        />
                    )}
                </div>
            )}
            <div className={styles.ButtonContainer}>
                {selectedApiClient ? (
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<ArrowBack />}
                        customText={t('backToList')}
                        onClick={() => {
                            refetch()
                            setSelectedApiClient(null)
                        }}
                        className={styles.Button}
                    />
                ) : (
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<ArrowBack />}
                        customText={t('backToParameters')}
                        onClick={backToParameters}
                        className={styles.Button}
                    />
                )}
            </div>
        </>
    )
}

export default ApiClientList
